import { ServiceSubcontractorService } from '@http/clients/reporting-app/service-subcontractor.service';
import { GridDisplayScheme, OverviewLayout } from '@pages/reports/types/report-layout-type.enum';
import { formatCurrencyCompact, formatNumbersCompact, truncateValue } from '@utils/format-functions';
import { AgChartLegendLabelFormatterParams } from 'ag-charts-community';
import { IReportConfiguration } from '../../types/report-config.interface';
import { ReportType } from '../../types/report-type.enum';
import { WidgetType } from '../../types/report-widget-type.enum';
import {
    marketsEditorConfig,
    opportunitySourceEditorConfig,
    projectDueDateEditorConfig,
    projectEndDateEditorConfig,
    projectStartDateEditorConfig,
    projectStatusEditorConfig
} from './shared/additional-filters';
import { salesMainFilters } from './shared/main-filters';

export const ServiceModuleSubcontractor: IReportConfiguration<ServiceSubcontractorService> = {
    base: {
        type: ReportType.ServiceModuleSubcontractors,
        serviceType: ServiceSubcontractorService
    },
    overviewTab: {
        widgets: [
            {
                methodName: 'getBoxesValues',
                type: WidgetType.Box,
                layoutTemplateArea: 'boxes'
            },
            {
                methodName: 'getMetricsValues',
                type: WidgetType.Metrics,
                layoutTemplateArea: 'metrics',
                settings: {
                    cols: [
                        { name: 'avg', label: 'Avg' },
                        { name: 'min', label: 'Min' },
                        { name: 'med', label: 'Med' },
                        { name: 'max', label: 'Max' }
                    ],
                    rows: [
                        { name: 'QuoteLumpSum', label: 'Quote Lump Sum' },
                        { name: 'QuoteUnitPricing', label: 'Quote Unit Pricing' },
                        { name: 'DirectCostLumpSum', label: 'DC $ Lump Sum', format: 'currency' },
                        { name: 'DirectCostUnitPricing', label: 'DC $ Unit Pricing', format: 'currency' },
                        { name: 'PercentageDirectCostLumpSum', label: 'TDC % Lump Sum', format: 'percent' },
                        {
                            name: 'PercentageTotalDirectCostUnitPricing',
                            label: 'TDC % Unit Pricing ',
                            format: 'percent'
                        }
                    ]
                }
            },
            {
                methodName: 'getLineChartValues',
                type: WidgetType.Chart,
                layoutTemplateArea: 'chart',
                settings: {
                    title: {
                        text: 'Top 10 Subcontractor by Total Sell Price'
                    },
                    series: [
                        {
                            type: 'line',
                            xKey: 'subcontractorName',
                            yKey: 'totalSellPrice',
                            yName: 'Total Sell Price'
                        },
                        {
                            type: 'line',
                            xKey: 'subcontractorName',
                            yKey: 'totalSellPriceLumpSum',
                            yName: 'Total Sell Price Lump Sum'
                        },
                        {
                            type: 'line',
                            xKey: 'subcontractorName',
                            yKey: 'totalSellPriceUnitPricing',
                            yName: 'Total Sell Price Unit Pricing'
                        },
                        {
                            type: 'line',
                            xKey: 'subcontractorName',
                            yKey: 'used',
                            yName: 'Number of Times Used'
                        }
                    ],
                    axes: [
                        {
                            type: 'category',
                            position: 'bottom',
                            label: {
                                formatter: (params: AgChartLegendLabelFormatterParams) =>
                                    truncateValue(params.value, 15),
                                rotation: 315
                            }
                        },
                        {
                            type: 'number',
                            position: 'left',
                            title: {
                                text: 'Amount'
                            },
                            keys: ['totalSellPrice', 'totalSellPriceLumpSum', 'totalSellPriceUnitPricing'],
                            label: {
                                formatter: (params: AgChartLegendLabelFormatterParams) =>
                                    formatCurrencyCompact(params.value)
                            }
                        },
                        {
                            type: 'number',
                            position: 'right',
                            title: {
                                text: 'Quantity'
                            },
                            keys: ['used'],
                            label: {
                                formatter: (params: AgChartLegendLabelFormatterParams) =>
                                    formatNumbersCompact(params.value)
                            }
                        }
                    ]
                }
            },
            {
                methodName: 'getColumnChartValues',
                type: WidgetType.Chart,
                layoutTemplateArea: 'chart2',
                settings: {
                    title: {
                        text: 'Salesperson Performance'
                    },
                    series: [
                        {
                            type: 'bar',
                            xKey: 'salesPersonName',
                            yKey: 'totalSellPrice',
                            yName: 'Total Sell Price'
                        },
                        {
                            type: 'bar',
                            xKey: 'salesPersonName',
                            yKey: 'totalSellPriceLumpSum',
                            yName: 'Total Sell Price Lump Sum'
                        },
                        {
                            type: 'bar',
                            xKey: 'salesPersonName',
                            yKey: 'totalSellPriceUnitPricing',
                            yName: 'Total Sell Price Unit Pricing'
                        }
                    ],
                    axes: [
                        {
                            type: 'category',
                            position: 'bottom',
                            label: {
                                formatter: (params: AgChartLegendLabelFormatterParams) =>
                                    truncateValue(params.value, 15),
                                rotation: 315
                            }
                        },
                        {
                            type: 'number',
                            position: 'left',
                            title: {
                                text: 'Amount'
                            },
                            keys: ['totalSellPrice', 'totalSellPriceLumpSum', 'totalSellPriceUnitPricing'],
                            label: {
                                formatter: (params: AgChartLegendLabelFormatterParams) =>
                                    formatCurrencyCompact(params.value)
                            }
                        }
                    ]
                }
            }
        ],
        layout: OverviewLayout.FourRowStack
    },
    gridTab: {
        widgets: [
            {
                methodName: 'getValues',
                type: WidgetType.Grid,
                layoutTemplateArea: 'grid',
                settings: [
                    { field: 'subcontractorName', headerName: 'Subcontractor Name', lockVisible: true },
                    { field: 'totalSellPrice', headerName: 'Total Sell Price', type: 'currency' },
                    { field: 'used', headerName: 'Number of times used' },
                    { field: 'avgSellPricePerUsed', headerName: 'Average Sell Price per Usage', type: 'currency' },
                    { field: 'qtyFinalEstimates', headerName: 'Number of Selected Final Estimates' },
                    {
                        field: 'avgSellPricePerFinalEstimates',
                        headerName: 'Average Sell Price per Selected Final Estimate',
                        type: 'currency'
                    },
                    { field: 'lumpSumQty', headerName: 'Number of times used Lump Sum' },
                    { field: 'unitPricingQty', headerName: 'Number of times used Unit Pricing' },
                    { field: 'totalSellPriceLumpSum', headerName: 'Total Sell Price Lump Sum', type: 'currency' },
                    {
                        field: 'totalSellPriceUnitPricing',
                        headerName: 'Total Sell Price Unit Pricing',
                        type: 'currency'
                    },
                    { field: 'avgQuoteLumpSum', headerName: 'Avg Quote Lump Sum' },
                    { field: 'minQuoteLumpSum', headerName: 'Min Quote Lump Sum' },
                    { field: 'medQuoteLumpSum', headerName: 'Med Quote Lump Sum' },
                    { field: 'maxQuoteLumpSum', headerName: 'Max Quote Lump Sum' },
                    { field: 'avgQuoteUnitPricing', headerName: 'Avg Quote Unit Pricing' },
                    { field: 'minQuoteUnitPricing', headerName: 'Min Quote Unit Pricing' },
                    { field: 'medQuoteUnitPricing', headerName: 'Med Quote Unit Pricing' },
                    { field: 'maxQuoteUnitPricing', headerName: 'Max Quote Unit Pricing' },
                    { field: 'avgDirectCostLumpSum', headerName: 'Avg DC $ Lump Sum', type: 'currency' },
                    { field: 'minDirectCostLumpSum', headerName: 'Min DC $ Lump Sum', type: 'currency' },
                    { field: 'medDirectCostLumpSum', headerName: 'Med DC $ Lump Sum', type: 'currency' },
                    { field: 'maxDirectCostLumpSum', headerName: 'Max DC $ Lump Sum', type: 'currency' },
                    { field: 'avgDirectCostUnitPricing', headerName: 'Avg DC $ Unit Pricing', type: 'currency' },
                    { field: 'minDirectCostUnitPricing', headerName: 'Min DC $ Unit Pricing', type: 'currency' },
                    { field: 'medDirectCostUnitPricing', headerName: 'Med DC $ Unit Pricing', type: 'currency' },
                    { field: 'maxDirectCostUnitPricing', headerName: 'Max DC $ Unit Pricing', type: 'currency' },
                    { field: 'avgPercentageDirectCostLumpSum', headerName: 'Avg DC % Lump Sum', type: 'percentage' },
                    { field: 'minPercentageDirectCostLumpSum', headerName: 'Min DC % Lump Sum', type: 'percentage' },
                    { field: 'medPercentageDirectCostLumpSum', headerName: 'Med DC % Lump Sum', type: 'percentage' },
                    { field: 'maxPercentageDirectCostLumpSum', headerName: 'Max DC % Lump Sum', type: 'percentage' },
                    {
                        field: 'avgPercentageDirectCostUnitPricing',
                        headerName: 'Avg DC % Unit Pricing',
                        type: 'percentage'
                    },
                    {
                        field: 'minPercentageDirectCostUnitPricing',
                        headerName: 'Min DC % Unit Pricing',
                        type: 'percentage'
                    },
                    {
                        field: 'medPercentageDirectCostUnitPricing',
                        headerName: 'Med DC % Unit Pricing',
                        type: 'percentage'
                    },
                    {
                        field: 'maxPercentageDirectCostUnitPricing',
                        headerName: 'Max DC % Unit Pricing',
                        type: 'percentage'
                    },
                    {
                        field: 'avgPercentageTotalDirectCostLumpSum',
                        headerName: 'Avg TDC % Lump Sum',
                        type: 'percentage'
                    },
                    {
                        field: 'minPercentageTotalDirectCostLumpSum',
                        headerName: 'Min TDC % Lump Sum',
                        type: 'percentage'
                    },
                    {
                        field: 'medPercentageTotalDirectCostLumpSum',
                        headerName: 'Med TDC % Lump Sum',
                        type: 'percentage'
                    },
                    {
                        field: 'maxPercentageTotalDirectCostLumpSum',
                        headerName: 'Max TDC % Lump Sum',
                        type: 'percentage'
                    },
                    {
                        field: 'avgPercentageTotalDirectCostUnitPricing',
                        headerName: 'Avg TDC % Unit Pricing',
                        type: 'percentage'
                    },
                    {
                        field: 'minPercentageTotalDirectCostUnitPricing',
                        headerName: 'Min TDC % Unit Pricing',
                        type: 'percentage'
                    },
                    {
                        field: 'medPercentageTotalDirectCostUnitPricing',
                        headerName: 'Med TDC % Unit Pricing',
                        type: 'percentage'
                    },
                    {
                        field: 'maxPercentageTotalDirectCostUnitPricing',
                        headerName: 'Max TDC % Unit Pricing',
                        type: 'percentage'
                    }
                ]
            }
        ],
        layout: GridDisplayScheme.Standard
    },
    filters: {
        main: [...salesMainFilters],
        additional: [
            projectDueDateEditorConfig,
            projectStartDateEditorConfig,
            projectEndDateEditorConfig,
            projectStatusEditorConfig,
            marketsEditorConfig,
            opportunitySourceEditorConfig
        ]
    }
};
